import { useEffect, useState } from "react";
import { LogIn, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmployeeAvatar } from "@/components/shared/EmployeeAvatar";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Pick } from "@/pages/gerencia/Sales";
import { beep, Card, OtSelect, savedOt, ScanBox, Screen, Toggle, useOts } from "./common";
import { cacheRoster, findWorker, normRut, send } from "./offline";

const isCarnet = (t) => /^https?:\/\//i.test(t) || /^[0-9.\s]{6,12}-?[0-9kK]$/.test(t);
const COND = [{ id: "buena", name: "Buena" }, { id: "danada", name: "Con daño" }, { id: "mantencion", name: "Mantención" }];

export default function Herramientas() {
  const { can } = useAuth();
  const [mode, setMode] = useState(can("abastecimiento.herramientas.entregar") ? "entrega" : "devolucion");
  const ots = useOts("/abastecimiento/ots");
  const whs = useOts("/abastecimiento/warehouses", (d) => d.map((w) => ({ id: w.id, name: `${w.code} · ${w.name}` })));
  const [ot, setOt] = useState(savedOt());
  const [wh, setWh] = useState("");
  const [worker, setWorker] = useState(null);
  const [cart, setCart] = useState([]);
  useEffect(() => { if (!wh && whs[0]) setWh(whs[0].id); }, [whs, wh]);
  useEffect(() => { cacheRoster(ot); }, [ot]);
  const reset = () => { setWorker(null); setCart([]); };
  const read = async (raw) => {
    const t = raw.trim();
    if (isCarnet(t)) {
      if (mode === "entrega" && !ot) return toast.error("Seleccione la OT");
      try { const { data } = await api.post("/abastecimiento/tools/identify", { qr: t, ot_id: mode === "entrega" ? ot : null }); beep(); setWorker({ ...data, qr: t }); setCart([]); }
      catch (e) {
        if (e.response) { beep(false); toast.error(errMsg(e)); return; }
        const w = findWorker(ot, t);
        beep(true); setWorker({ employee: w || { id: null, name: `RUT ${normRut(t)}` }, holding: [], enablement: "habilitado", qr: t, offline: true }); setCart([]);
      }
      return;
    }
    if (!worker) { beep(false); return toast.error("Primero escanee el carnet"); }
    const code = t.toUpperCase();
    if (cart.some((c) => c.code === code)) return;
    try { const { data } = await api.get(`/abastecimiento/tools/${code}`); beep(); setCart((c) => [...c, { code: data.code, name: data.product_name, status: data.status, condition: "buena" }]); }
    catch (e) {
      if (e.response) { beep(false); toast.error(errMsg(e)); return; }
      beep(true); setCart((c) => [...c, { code, name: "Sin señal: se valida al enviar", status: "disponible", condition: "buena" }]);
    }
  };
  const confirm = async () => {
    try {
      const label = `${mode === "entrega" ? "Entrega" : "Recepción"} a ${worker.employee.name}`;
      const r = mode === "entrega"
        ? await send("/abastecimiento/tools/deliver", { qr: worker.qr, ot_id: ot, codes: cart.map((c) => c.code) }, label)
        : await send("/abastecimiento/tools/return", { qr: worker.qr, warehouse_id: wh, items: cart.map(({ code, condition }) => ({ code, condition, note: "" })) }, label);
      if (r.queued) { toast.success("Guardado sin señal: se enviará al volver la señal"); reset(); return; }
      const data = r.data;
      const ok = data.results.filter((r) => r.ok).length;
      const bad = data.results.filter((r) => !r.ok);
      ok ? toast.success(`${ok} herramienta(s) ${mode === "entrega" ? "entregadas" : "recibidas"}`) : toast.error("No se registró ninguna");
      bad.forEach((r) => toast.error(`${r.code}: ${r.error}`));
      reset();
    } catch (e) { if (!e.quota) toast.error(errMsg(e)); }
  };
  const blocked = mode === "entrega" && worker && !["habilitado", "observado", "habilitado_excepcion"].includes(worker.enablement);
  const modes = [["entrega", "ENTREGAR", LogOut], ["devolucion", "RECIBIR", LogIn]].filter(([k]) => can(k === "entrega" ? "abastecimiento.herramientas.entregar" : "abastecimiento.devoluciones.recibir"));
  return (
    <Screen title="Herramientas" testid="terreno-tools">
      {modes.length > 1 && <Toggle testid="terreno-tools-mode" value={mode} onChange={(m) => { setMode(m); reset(); }} options={modes} />}
      {mode === "entrega" ? <OtSelect testid="terreno-tools-ot" ots={ots} value={ot} onChange={(v) => { setOt(v); reset(); }} />
        : <Pick testid="terreno-tools-wh" value={wh} onChange={setWh} options={whs} placeholder="Bodega que recibe" />}
      <div className="text-sm font-semibold text-[#258F00]" data-testid="terreno-tools-step">{worker ? "2. Escanee las herramientas, una tras otra" : "1. Escanee el carnet del trabajador"}</div>
      <ScanBox onRead={read} placeholder={worker ? "O escriba el código HER-… y presione Ir" : "O escriba el RUT y presione Ir"} testid="terreno-tools-scan" />
      {worker && <Card className="space-y-3" >
        <div className="flex items-center gap-3" data-testid="terreno-tools-worker">
          {worker.employee.id && <EmployeeAvatar id={worker.employee.id} v={worker.employee.photo_v} name={worker.employee.name} size={52} />}
          <div className="flex-1 min-w-0"><div className="font-extrabold truncate">{worker.employee.name}</div><div className="text-xs text-[#5b6656]">{worker.offline ? "Sin señal: la habilitación se valida al enviar" : `Tiene ${worker.holding.length} herramienta(s)`}</div></div>
          <button onClick={reset} data-testid="terreno-tools-clear"><X size={20} /></button>
        </div>
        {blocked && <div className="text-sm font-semibold text-[#b42318]">No habilitado en la OT: no se pueden entregar herramientas.</div>}
        {cart.map((c, i) => (
          <div key={c.code} data-testid={`terreno-tool-${c.code}`} className="flex items-center gap-2 border-t border-[#f0f2ee] pt-2 text-sm">
            <div className="flex-1 min-w-0"><div className="font-mono text-xs">{c.code}</div><div className="truncate">{c.name}</div>
              {mode === "entrega" && c.status !== "disponible" && <div className="text-xs text-[#b42318]">{c.status}</div>}</div>
            {mode === "devolucion" && <div className="w-32"><Pick testid={`terreno-tool-cond-${c.code}`} value={c.condition} onChange={(v) => setCart(cart.map((x, j) => (j === i ? { ...x, condition: v } : x)))} options={COND} /></div>}
            <button onClick={() => setCart(cart.filter((_, j) => j !== i))}><X size={18} /></button>
          </div>))}
        <Button data-testid="terreno-tools-confirm" className="w-full h-14 rounded-2xl text-lg font-extrabold" disabled={!cart.length || blocked} onClick={confirm}>
          Confirmar {mode === "entrega" ? "entrega" : "recepción"} ({cart.length})
        </Button>
      </Card>}
    </Screen>
  );
}
